import React from 'react';
import { Box } from '@wix/design-system';
import {
  useCurrentModule,
  ECLIPSE_PRIMARY_COLOR,
  ECLIPSE_BORDER_COLOR,
  useSelectButton,
  useProductHat,
} from '@wix/package-picker-shared-eclipse';
import { SwitchProductModule } from '@wix/package-picker-sdk';
import { ProductCardList } from '@wix/premium-purchase-platform/@components';
import { SocialProofHat } from './SocialProofHat';
import { CardContent } from './CardContent';

interface SocialProofProductCardProps {
  productId: string;
}

export const SocialProofProductCard = ({
  productId,
}: SocialProofProductCardProps) => {
  const module = useCurrentModule();
  const { ProductHat } = useProductHat({ productId });
  const { data: product } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({ productId });
  const { data: currentProductData } =
    SwitchProductModule.SDK.switchApiClient.hooks.useGetCurrentProduct({});
  const { data: offeringDiscount } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchOfferingDiscount({});
  const { name, isRecommended } = product ?? {};
  const hasSale = offeringDiscount?.hasSale ?? false;
  const hasCurrentProductInOffering =
    currentProductData?.isCurrentProductInOffering ?? false;
  const { SelectButton } = useSelectButton({ productId });
  const { t } = module.internals.useTranslations();

  const borderValue = isRecommended
    ? `2px solid ${ECLIPSE_PRIMARY_COLOR}`
    : `1px solid ${ECLIPSE_BORDER_COLOR}`;

  return (
    <ProductCardList.Default.Card.Root
      width="100%"
      paddingBottom="40px"
      maxWidth="370px"
      minHeight="450px"
      borderRadius={isRecommended ? '0 0 16px 16px' : '16px'}
      borderTop={isRecommended ? 'none' : borderValue}
      borderBottom={borderValue}
      borderLeft={borderValue}
      borderRight={borderValue}
      {...(isRecommended
        ? { clipPath: 'none', overflow: 'visible', position: 'relative' }
        : {})}
    >
      {isRecommended && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '-2px',
            right: '-2px',
          }}
        >
          <SocialProofHat
            text={t('premium-purchase-plan.eclipse.cards.hat.social-proof')}
            borderTop={borderValue}
            borderLeft={borderValue}
            borderRight={borderValue}
            borderBottom="none"
            borderRadius="16px 16px 0 0"
          />
        </div>
      )}
      {(hasSale || hasCurrentProductInOffering) && (
        <Box
          marginTop="20px"
          style={{ alignSelf: 'stretch' }}
          paddingLeft="20px"
          paddingRight="20px"
        >
          <ProductHat />
        </Box>
      )}
      <Box
        width="100%"
        direction="vertical"
        boxSizing="border-box"
        paddingTop="20px"
        flexGrow={1}
        paddingLeft="20px"
        paddingRight="20px"
      >
        <CardContent
          name={name}
          isRecommended={isRecommended}
          SelectButton={SelectButton}
          productId={productId}
        />
      </Box>
    </ProductCardList.Default.Card.Root>
  );
};
